/* Altara UNDERSTAND · contextual education + signal tracking · slide asset (~14s)
 * Beat 1: proposal in view.
 * Beat 2: Nomsa asks 3 in-context questions to NAVI; NAVI answers briefly.
 * Beat 3: Right panel shows each question as a tracked wallet signal.
 * Beat 4: Signals are packaged into a Learning Intervention profile. */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, ArrowRight, RotateCcw, Play, Landmark, BadgeCheck,
  Activity, GraduationCap, BookOpen, Bell, Hash, Clock, User,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";

/* Deterministic content — Nomsa's actual in-context questions */
const CHAT = [
  {
    q: "What does \u201Cprojected 11.5% p.a.\u201D actually mean for my R450k?",
    a: "It's an assumption, not a promise. If that assumption held for 5 years, R450k contributions could grow to about R604k — but it could also be lower. It's not guaranteed.",
    signal: { topic: "returns_projection", concept: "expected vs guaranteed", severity: "clarify" },
  },
  {
    q: "What happens if I need to stop or take money out before 60 months?",
    a: "An early-exit fee applies and the tax-advantage lapses. Ask James for the exact early-exit table before you decide.",
    signal: { topic: "early_exit_rules", concept: "liquidity + penalties", severity: "clarify" },
  },
  {
    q: "The 0.98% p.a. fee — is that low or high?",
    a: "It's on the lower side for a balanced fund. But over 5 years on R604k, 0.98% p.a. still compounds to roughly R14 800 — real money, so worth understanding.",
    signal: { topic: "fees_compounding", concept: "small % → large rand impact", severity: "educate" },
  },
];

const INTERVENTIONS = [
  { title: "Micro-lesson · 2 min", body: "\u201CWhat fees really cost you over 5 years\u201D", Icon: BookOpen },
  { title: "Follow-up prompt", body: "\u201CBefore Act, ask James for the early-exit table.\u201D", Icon: Bell },
  { title: "Adviser signal", body: "James Smith notified: Nomsa asked 2 fees questions — offer a fees walk-through.", Icon: User },
];

export default function WalletAltaraUnderstandDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") === "1";
  if (embed) {
    return (
      <div className="w-screen h-screen flex items-center justify-center px-6 py-6 overflow-hidden"
           style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}
           data-testid="altara-understand-embed">
        <div className="w-full max-w-[1400px]">
          <EmbedHeader />
          <div className="mt-4"><UnderstandStage autoplay={autoplay || true} /></div>
        </div>
        <SlideBrandMark />
      </div>
    );
  }
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="altara-understand">
        <Header />
        <DownloadStrip />
        <UnderstandStage />
        <Footnote />
      </div>
    </WalletShell>
  );
}

function Header() {
  return (
    <header>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Demo Simulation</div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>
        Understand · contextual education, tracked as learning signals
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-3xl">
        Education isn&apos;t a course. It happens right there, on the proposal Nomsa is looking at.
        Every question she asks is captured as a signal in her wallet and packaged into
        personalised learning interventions — for her, and for her adviser.
      </p>
    </header>
  );
}
function EmbedHeader() {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
        Altara Consumer Intelligence · Understand
      </div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold mt-0.5" style={{ color: NAVY }}>
        Contextual education · packaged as learning interventions
      </h1>
      <div className="text-[13px] text-slate-600 mt-1">
        Every question Nomsa asks becomes a tracked signal — and a lesson she actually needs.
      </div>
    </div>
  );
}
function SlideBrandMark() {
  return (
    <div className="absolute bottom-3 right-5 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: NAVY, opacity: 0.55 }}>
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co
    </div>
  );
}
function DownloadStrip() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Slide assets</div>
        <div className="text-sm text-slate-700 mt-0.5">MP4 for modern PPT · GIF for universal embedding.</div>
      </div>
      <a href="/altara-understand.mp4" download data-testid="dl-mp4"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white"
         style={{ background: NAVY }}>Download MP4 <ArrowRight className="w-3.5 h-3.5" /></a>
      <a href="/altara-understand.gif" download data-testid="dl-gif"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold border"
         style={{ background: "white", borderColor: NAVY, color: NAVY }}>
        Download GIF <ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}
function Footnote() {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[12.5px] text-slate-600 leading-relaxed">
      <strong className="text-slate-800">What Altara does:</strong> turn each in-context question into a
      wallet signal, and every signal into an intervention Nomsa actually needs.
      <strong className="text-slate-800 ml-1">What Altara does not:</strong> replace advice.
      NAVI explains — the adviser advises.
    </div>
  );
}

/* ═════════════ STAGE ═════════════════════ */
function UnderstandStage({ autoplay = false }) {
  const [phase, setPhase] = useState("idle");   // idle | chatting | packaging | done
  const [chatIdx, setChatIdx] = useState(-1);   // -1..2 (also step within pair · see typing)
  const [answered, setAnswered] = useState(-1); // -1..2  answer visible up to this index
  const [signals, setSignals] = useState([]);   // list of visible signals
  const [interventionIdx, setInterventionIdx] = useState(-1);
  const [profileVisible, setProfileVisible] = useState(false);
  const timers = useRef([]);
  const fired = useRef(false);
  const t = (ms, fn) => timers.current.push(setTimeout(fn, ms));
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const reset = () => {
    clear();
    setPhase("idle"); setChatIdx(-1); setAnswered(-1);
    setSignals([]); setInterventionIdx(-1); setProfileVisible(false);
  };
  const play = () => {
    if (phase !== "idle" && phase !== "done") return;
    reset();
    setPhase("chatting");
    // Beat 2 · 3 Q/A pairs, each about 2.3s.
    const perPair = 2300;
    for (let i = 0; i < CHAT.length; i++) {
      const start = 400 + i * perPair;
      t(start,        () => setChatIdx(i));                          // Q appears
      t(start + 1100, () => setAnswered(i));                         // A appears
      t(start + 1500, () => setSignals(s => [...s, CHAT[i].signal])); // signal ticker fires
    }
    // Beat 4 · package interventions
    const pkgStart = 400 + CHAT.length * perPair;
    t(pkgStart,          () => setPhase("packaging"));
    t(pkgStart + 600,    () => setInterventionIdx(0));
    t(pkgStart + 1400,   () => setInterventionIdx(1));
    t(pkgStart + 2200,   () => setInterventionIdx(2));
    t(pkgStart + 2900,   () => setProfileVisible(true));
    t(pkgStart + 3600,   () => setPhase("done"));
  };

  useEffect(() => {
    if (!autoplay || fired.current) return;
    fired.current = true;
    setTimeout(play, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);
  useEffect(() => () => clear(), []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-4"
         data-testid="understand-stage">
      {/* LEFT · in-context chat with NAVI */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        {/* Proposal context strip */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3"
             style={{ background: `${NAVY}05` }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${NAVY}12` }}>
            <Landmark className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9.5px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Proposal in view</div>
            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>ABC Wealth Balanced Growth Portfolio · R 450 000 over 60 months</div>
          </div>
          <StageChip />
        </div>
        {/* NAVI chat */}
        <div className="p-5 space-y-3 min-h-[440px]">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: NAVY }}>
              <Sparkles className="w-4 h-4" style={{ color: GOLD }} />
            </div>
            <div>
              <div className="text-[9.5px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>NAVI · explains · never advises</div>
              <div className="text-[12.5px] text-slate-600">Ask anything about this proposal — I&apos;ll answer in plain language.</div>
            </div>
          </div>
          {CHAT.map((c, i) => (
            <div key={i} className="space-y-1.5">
              <AnimatePresence>
                {chatIdx >= i && (
                  <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3 }}
                              data-testid={`chat-q-${i}`}
                              className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm px-3 py-2 text-[13px]"
                              style={{ background: NAVY, color: "white" }}>
                    {c.q}
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {answered >= i && (
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3 }}
                              data-testid={`chat-a-${i}`}
                              className="mr-auto max-w-[88%] rounded-2xl rounded-tl-sm px-3 py-2 text-[13px] border"
                              style={{ background: `${GOLD}12`, borderColor: `${GOLD}55`, color: NAVY }}>
                    <span className="text-[9px] font-bold tracking-widest mr-1.5 px-1.5 py-0.5 rounded"
                          style={{ background: GOLD, color: NAVY }}>NAVI</span>
                    {c.a}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT · signals feed + intervention pack */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        {/* Signals feed */}
        <div className="px-5 py-3 flex items-center gap-3 border-b border-slate-100"
             style={{ background: `${GOLD}0F` }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: GOLD }}>
            <Activity className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div className="flex-1">
            <div className="text-[9.5px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Wallet signals · live</div>
            <div className="text-[12.5px] text-slate-600">Every question Nomsa asks is tracked, timestamped and hashed.</div>
          </div>
          <div className="text-[10px] font-mono text-slate-500">
            {signals.length}/{CHAT.length}
          </div>
        </div>
        <div className="px-5 py-3 space-y-1.5 flex-1 min-h-[200px]">
          <AnimatePresence>
            {signals.map((s, i) => (
              <motion.div key={i}
                          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          data-testid={`signal-${i}`}
                          className="rounded-lg border px-3 py-2 flex items-center gap-2 text-[11.5px]"
                          style={{ borderColor: "#E2E8F0", background: "white" }}>
                <Hash className="w-3 h-3 flex-shrink-0" style={{ color: GOLD }} />
                <span className="font-mono text-slate-500 text-[10.5px]">
                  {`14:${(7 + i).toString().padStart(2, "0")}`}
                </span>
                <span className="font-mono px-1.5 py-0.5 rounded text-[9.5px] font-semibold flex-shrink-0"
                      style={{ background: NAVY, color: "white" }}>question_asked</span>
                <span className="font-mono text-[10.5px] text-slate-700 truncate">
                  topic:<span className="font-semibold">{s.topic}</span> · sev:<span style={{ color: s.severity === "educate" ? "#B45309" : NAVY }}>{s.severity}</span>
                </span>
              </motion.div>
            ))}
          </AnimatePresence>
          {signals.length === 0 && (
            <div className="text-[11.5px] text-slate-400 italic mt-2">Waiting for the first question…</div>
          )}
        </div>

        {/* Intervention pack */}
        <div className="border-t border-slate-100 p-5"
             style={{ background: phase === "packaging" || phase === "done" ? "#FDFCF9" : "white" }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ background: (phase === "packaging" || phase === "done") ? "#059669" : "#E2E8F0" }}>
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-[9.5px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Learning intervention pack</div>
              <div className="text-[12.5px] font-semibold" style={{ color: NAVY }}>
                {phase === "done" ? "Sealed into Nomsa\u2019s wallet + adviser inbox"
                                  : phase === "packaging" ? "Packaging your signals…"
                                  : "Waiting for signals"}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {INTERVENTIONS.map((iv, i) => (
              <AnimatePresence key={i}>
                {interventionIdx >= i && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                              data-testid={`intervention-${i}`}
                              className="rounded-lg border px-2.5 py-2 flex items-start gap-2"
                              style={{ borderColor: "#E2E8F0", background: "white" }}>
                    <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                         style={{ background: `${GOLD}22` }}>
                      <iv.Icon className="w-3.5 h-3.5" style={{ color: NAVY }} />
                    </div>
                    <div>
                      <div className="text-[10.5px] tracking-[0.2em] uppercase font-semibold" style={{ color: GOLD }}>{iv.title}</div>
                      <div className="text-[11.5px]" style={{ color: NAVY }}>{iv.body}</div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            ))}
          </div>
          <AnimatePresence>
            {profileVisible && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35 }}
                          data-testid="learning-profile"
                          className="mt-3 rounded-lg p-2.5 flex items-center gap-2"
                          style={{ background: "#ECFDF5", border: "1px solid #6EE7B7" }}>
                <BadgeCheck className="w-4 h-4 flex-shrink-0" style={{ color: "#059669" }} />
                <div className="text-[11.5px] leading-snug" style={{ color: "#065F46" }}>
                  <strong>Learning profile updated:</strong> gap detected in <em>fees + liquidity</em>.
                  <span className="ml-1 text-slate-600">Filed as evidence in Nomsa&apos;s Trust Wallet.</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Controls · bottom */}
      <div className="lg:col-span-2 flex items-center justify-end gap-2">
        {phase !== "idle" && (
          <button onClick={reset} data-testid="reset-understand"
                  className="w-8 h-8 rounded-full flex items-center justify-center border"
                  style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={play} disabled={phase === "chatting" || phase === "packaging"}
                data-testid="play-understand"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                style={{ background: NAVY }}>
          <Play className="w-3.5 h-3.5" /> {phase === "idle" ? "Play" : phase === "done" ? "Replay" : "Playing…"}
        </button>
      </div>
    </div>
  );
}

function StageChip() {
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold"
         style={{ background: NAVY, color: "white", boxShadow: `0 0 0 3px ${GOLD}44` }}>
      <BadgeCheck className="w-3.5 h-3.5" style={{ color: GOLD }} /> Understand
    </div>
  );
}
